import { AriApiQueryCreatorFetch } from '../AriApiCreator';
import { prismScopes } from '../auth';
import type { LandUseAggregate, LandUseAggregateMap } from './models';

// Same APIM path segment the ArcGIS-native map layer requests already use
// (see packages/map/src/services.ts BaseGisService) for the `ari-esri-proxy`
// Function app root — but unlike the ArcGIS-native passthrough (routed under
// `esriproxy/rest/services/*`), these plain-JSON land-use cache routes are
// registered at the Function app root (`landuse`, `landuse/{propertyId}`),
// NOT under an `esriproxy/` prefix. Proxy (ArcGIS passthrough) routes hit
// `esriproxy/...`; these landuse routes intentionally do not.
//
// Local-dev override: set VITE_ESRI_PROXY_USE_LOCALHOST=true +
// VITE_ESRI_PROXY_LOCALHOST_URL in apps/prism2/.env.local to point both this
// service AND @repo/map's BaseGisService at the same locally-running
// ari-esri-proxy Function host (defaults to the port its own
// launchSettings.json runs on, http://localhost:7081/api). Never set these in
// .env.production.
const useLocalEsriProxy = import.meta.env.VITE_ESRI_PROXY_USE_LOCALHOST === 'true';
const localEsriProxyBase = (import.meta.env.VITE_ESRI_PROXY_LOCALHOST_URL as string | undefined) || 'http://localhost:7081/api';
const url = useLocalEsriProxy ? localEsriProxyBase : `${import.meta.env.VITE_API_MGR_BASE_URL}/ari-esri-proxy`;

/**
 * An RTK Query API slice for the land-use aggregate cache, providing cached geospatial land-use breakdown data by property.
 *
 * Built on a locally configured base URL via VITE_ESRI_PROXY_USE_LOCALHOST / VITE_ESRI_PROXY_LOCALHOST_URL for local development overrides.
 */
const LandUseApi = AriApiQueryCreatorFetch({
  scopesObj: prismScopes,
  // @ts-ignore
  serviceURL: url,
  apiOptions: {
    reducerPath: 'landUseApi',
    keepUnusedDataFor: 600,
    endpoints: (builder) => ({
      getLandUseAll: builder.query<LandUseAggregateMap, void>({
        query: () => '/landuse',
      }),
      getLandUseByPropertyId: builder.query<LandUseAggregate, string>({
        query: (propertyId) => `/landuse/${propertyId}`,
      }),
    }),
  },
});

/**
 * The exported RTK Query API slice for the land-use aggregate cache, providing cached geospatial land-use breakdown data by property.
 */
export const landUseApi = LandUseApi;

export const { useGetLandUseAllQuery, useGetLandUseByPropertyIdQuery } = LandUseApi.hooks;
